import { autobind } from 'core-decorators';
import { detect } from 'detect-browser';
import update from 'immutability-helper';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';
import { Layout } from 'app/blocks/blocks';
import { processing, tryCatch } from 'app/blocks/common/decorators';
import showDialog from 'app/blocks/common/jsx/dialogModal';
import { compose, getQueryParamSafe, supportCreditCardPayment } from 'app/blocks/common/utils';
import withAuth from 'app/blocks/common/withAuth';
import withConfirmLeaving from 'app/blocks/common/withConfirmLeaving';
import middlewareCPS from 'app/blocks/middleware/cps';
import * as middleware from 'app/blocks/middleware/middleware';
import { saveForLater } from 'app/blocks/middleware/price-proposal';
import middlewareWPG from 'app/blocks/middleware/wpg';
import LicenseSigningProcessContext from 'app/pages/license-signing-process/context/Context';
import withContext from 'app/pages/license-signing-process/context/withContext';
import { getContactState } from 'app/pages/license-signing-process/DiscountAndBillingDetails/utils';
import { confirmDraftDeletion } from 'app/pages/license-signing-process/Payment/utils';
import StepEnum from 'app/pages/license-signing-process/StepEnum';
import flow from 'app/pages/orders/orders.flow';
import payment from 'app/pages/orders/orders.payment';
import { confirmOrderChanges, notifyUserThatOrderIsSaved } from 'app/pages/orders/orders.utils';
import routes from 'app/pages/routes';
import PaymentPanelEnum from '../PaymentPanelEnum';
import PaymentStepEnum from '../PaymentStepEnum';

const Context = React.createContext({});

function resetInititateState(initiate, order) {
    return update(initiate, {
        [PaymentPanelEnum.DISCOUNT]: { discountDetails: { $set: { ...order?.discountDetails } } },
        [PaymentPanelEnum.CONTACT]: (function () {
            const addresses = getContactState(order);

            return _.object(
                Object.keys(addresses),
                Object.values(addresses).map(value => ({ $set: value })),
            );
        })(),
        [PaymentPanelEnum.VAT_TAX]: { vatTaxDetails: { $set: { ...order?.vatTaxDetails } } },
        statusPopupError: { $set: undefined },
    });
}

function withProvider(WrappedComponent) {
    @autobind
    class GOAPaymentContext extends React.PureComponent {
        static propTypes = {
            article: PropTypes.shape({ id: PropTypes.string }).isRequired,
            canChangePaymentMethod: PropTypes.bool.isRequired,
            canDeleteDraft: PropTypes.bool.isRequired,
            confirmPayment: PropTypes.func.isRequired,
            confirmPaymentMethod: PropTypes.func.isRequired,
            deleteOnlineOpenDraft: PropTypes.func.isRequired,
            isOrderExisting: PropTypes.bool,
            journal: PropTypes.shape({}).isRequired,
            location: PropTypes.shape({ href: PropTypes.string }),
            order: PropTypes.shape({}).isRequired,
            reload: PropTypes.func.isRequired,
            setChangesChecker: PropTypes.func.isRequired,
            substep: PropTypes.string,
            unsetChangesChecker: PropTypes.func.isRequired,
        };

        static defaultProps = {
            isOrderExisting: false,
            location: window.location,
            substep: PaymentStepEnum.INITIATE,
        };

        state = {
            ignoredCpsErrors: ['CANCELLED'],
            initiate: {
                [PaymentPanelEnum.DISCOUNT]: {},
                [PaymentPanelEnum.CONTACT]: {},
                [PaymentPanelEnum.VAT_TAX]: {},
            },
            isProcessing: true,
            prices: [],
            review: {},
            specificCpsErrors: ['TIMEOUT', 'FAILED'],
        };

        @tryCatch.showPopUpAndGoToDashboard
        @processing
        async componentDidMount() {
            const params = getQueryParamSafe(this.props.location.href);

            let cpsError;
            if (params.code) {
                if (params.status) {
                    cpsError = params.status;
                } else {
                    showDialog.error({
                        code: params.code,
                        message: params.message && decodeURIComponent(params.message.replace(/\+/g, '%20')),
                        refId: params.refId,
                    });
                }
            }

            const socopayEnabled = middleware.payment.getSocopayEnabled();
            const { order } = this.props;

            this.setState(state => ({
                cpsError,
                initOrder: { ...order },
                initiate: resetInititateState(state.initiate, order),
                order,
                socopayEnabled,
            }));

            this.panels = [];

            if (params.scrollTo) {
                setTimeout(() => this.scrollTo(params.scrollTo), 300);
            }
        }

        setInitiateState(patch, callback = () => {}) {
            this.setState(state => ({ initiate: { ...state.initiate, ...patch(state.initiate) } }), callback);
        }

        setReviewState(patch) {
            this.setState(state => ({ review: { ...state.review, ...patch } }));
        }

        // initiate things
        registerPanel(panel) {
            this.panels.push(panel);
        }

        unregisterPanel(panel) {
            this.panels = this.panels.filter(x => x.panelName === panel.panelName);
        }

        scrollTo(type) {
            this.panels.find(panel => panel.panelName === type).scrollToPanel();
        }

        updatePaymentDetails(patch, callback) {
            this.setInitiateState(state => ({ payment: { ...state.payment, ...patch } }), callback);
        }

        updateValidationResults(validationResults) {
            this.setState(state => {
                const initiate = { ...state.initiate, payment: { ...state.initiate?.payment, validationResults } };
                return { ...state, initiate };
            });
        }

        updatePaymentMethod(paymentMethod, skipPONumber) {
            const { order = {}, paymentDetails = {} } = this.state;
            let patch = {};
            if (skipPONumber) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { poNumber, ...otherPaymentDetails } = paymentDetails;
                patch = { paymentDetails: { ...otherPaymentDetails, paymentMethod } };
            } else {
                patch = { paymentDetails: { ...paymentDetails, paymentMethod } };
            }
            const newOrder = { ...order, paymentMethod };

            this.setState(state => ({
                ...state,
                initiate: { ...state.initiate, payment: { ...state.initiate?.payment, ...patch } },
                order: newOrder,
            }));
        }

        isPaymentMethodSupportedByBrowser() {
            return (
                this.state?.initiate?.payment?.paymentDetails?.paymentMethod !== payment.method.CREDIT_CARD.key ||
                supportCreditCardPayment(detect())
            );
        }

        isSaveAndPreviewAllowed() {
            if (!this.isPaymentMethodSupportedByBrowser()) {
                return false;
            }

            return [get(this.state, 'initiate.payment.paymentDetails.paymentMethod')].every(x => !!x);
        }

        getOrderWithUpdates() {
            return {
                ...this.state.order,
                correspondenceAddress: get(this.state, 'initiate.addresses.mainAddress'),
                discountDetails: get(this.state, 'initiate.discount.discountDetails'),
                paymentDetails: get(this.state, 'initiate.payment.paymentDetails'),
                vatTaxDetails: get(this.state, 'initiate.vatTax.vatTaxDetails'),
            };
        }

        @tryCatch.popUpOnly
        @processing('initiate.isDraftBeingDeleted')
        async deleteDraft() {
            if (await confirmDraftDeletion()) {
                await this.props.deleteOnlineOpenDraft();
            }
        }

        @tryCatch.popUpOnly
        @processing('initiate.isSavingForLater')
        async saveForLater() {
            const order = this.getOrderWithUpdates();
            const { articleId, billingAddress = {}, paymentDetails = {}, vatTaxDetails = {} } = order;
            await saveForLater(articleId, { billingAddress, paymentDetails, vatTaxDetails });
            this.setState({ order });
            await notifyUserThatOrderIsSaved('ONLINE_OPEN', 'initiate');

            this.props.unsetChangesChecker();

            routes.navigateToUrl(`/index.html/article/${articleId}`);
        }

        // review things
        onRequestEditOrder(panel) {
            if (panel === PaymentPanelEnum.PAYMENT) {
                routes.navigateToUrl(
                    `/index.html/license-signing/${this.props.article.id}/${StepEnum.PAYMENT}/${PaymentStepEnum.REVIEW_FINAL_CHARGES}`,
                );
                setTimeout(() => this.scrollTo(panel), 300);
            } else {
                routes.navigateToUrl(
                    `/index.html/license-signing/${this.props.article.id}/${StepEnum.DISCOUNT_AND_BILLING}`,
                    { replace: false },
                    panel,
                );
            }
        }

        onPaymentDataCorrectChange(isPaymentDataCorrect) {
            this.setReviewState({ isPaymentDataCorrect });
        }

        onSocopayAgreeChange(isSocopayDataCorrect) {
            this.setReviewState({ isSocopayDataCorrect });
        }

        onReviewCancel() {
            routes.navigateToUrl(
                `/index.html/license-signing/${this.props.article.id}/${StepEnum.PAYMENT}/${PaymentStepEnum.REVIEW_FINAL_CHARGES}`,
            );
        }

        async updateOrderWithPaymentMethod() {
            return {
                ...this.state.order,
                paymentDetails: get(this.state, 'initiate.payment.paymentDetails'),
            };
        }

        @tryCatch.popUpOnly
        @processing('initiate.isConfirming')
        async onReviewFinalChargesConfirm() {
            try {
                const { isOrderExisting } = this.props;
                const currentOrder = await this.updateOrderWithPaymentMethod();
                const { order } = await this.props.confirmPaymentMethod(currentOrder, isOrderExisting);

                this.setState(
                    state => ({
                        initiate: resetInititateState(state.initiate, order),
                        order,
                    }),
                    async () => {
                        routes.navigateToUrl(
                            `/index.html/license-signing/${this.props.article.id}/${StepEnum.PAYMENT}/${PaymentStepEnum.REVIEW}`,
                        );
                    },
                );
            } catch (error) {
                // instanceof does not work
                if (error.name === 'ValidationError') {
                    this.setInitiateState(() => ({
                        statusPopupError: new Error(
                            'There was an error with your submission. Please scroll down to review',
                        ),
                    }));
                } else if (error.message === 'VatTaxShouldBeReentered') {
                    this.scrollTo(PaymentPanelEnum.VAT_TAX);
                } else {
                    throw error;
                }
            }
        }

        @tryCatch.popUpOnly
        @processing('review.isConfirming')
        async onReviewConfirm() {
            const { isOrderExisting } = this.props;
            const { order } = this.state;

            if (isOrderExisting && !(await confirmOrderChanges('ONLINE_OPEN'))) {
                return;
            }

            if (flow.isCheckoutNeededByDraftGOA(order, isOrderExisting)) {
                this.props.onPaymentCheckout();
                if (flow.isPaymentMethodSocopay(order)) {
                    await middlewareCPS.getCPSConfigAndSubmitForm(order.chinaPayPricingDetails.socopayOrderNumber);
                } else {
                    await middlewareWPG.getOnlineOpenWPGConfigAndSubmitForm(order.articleId);
                }
            } else {
                await this.props.confirmPayment(order);
            }
        }

        render() {
            if (this.state.isProcessing) {
                return <Layout isLoading />;
            }

            return (
                <Context.Provider
                    value={{
                        ...this.state,
                        article: this.props.article,
                        canChangePaymentMethod: this.props.canChangePaymentMethod,
                        canDeleteDraft: this.props.canDeleteDraft,
                        chinaOaEnabled: this.props.chinaOaEnabled,
                        isChinaOaWithForeignBilling:
                            this.props.chinaOaEnabled &&
                            !!this.state.order?.billingAddress?.countryCode &&
                            this.state.order.billingAddress.countryCode !== 'CN',
                        deleteDraft: this.deleteDraft,
                        initiate: {
                            ...this.state.initiate,
                            isSaveAndPreviewAllowed: this.isSaveAndPreviewAllowed(),
                        },
                        isCheckoutNeeded: flow.isCheckoutNeededByDraftGOA(this.state.order, this.props.isOrderExisting),
                        isOrderExisting: this.props.isOrderExisting,
                        isPaymentMethodSupportedByBrowser: this.isPaymentMethodSupportedByBrowser(),
                        journal: this.props.journal,
                        onPaymentDataCorrectChange: this.onPaymentDataCorrectChange,
                        onRequestEditOrder: this.onRequestEditOrder,
                        onReviewCancel: this.onReviewCancel,
                        onReviewConfirm: this.onReviewConfirm,
                        onReviewFinalChargesConfirm: this.onReviewFinalChargesConfirm,
                        onSocopayAgreeChange: this.onSocopayAgreeChange,
                        paymentDetails: this.state?.initiate?.payment?.paymentDetails,
                        registerPanel: this.registerPanel,
                        saveForLater: this.saveForLater,
                        unregisterPanel: this.unregisterPanel,
                        updatePaymentDetails: this.updatePaymentDetails,
                        updatePaymentMethod: this.updatePaymentMethod,
                        updateValidationResults: this.updateValidationResults,
                    }}
                >
                    <WrappedComponent {...this.props} />
                </Context.Provider>
            );
        }
    }

    return GOAPaymentContext;
}

const useOnlineOpenPaymentFlow = () => React.useContext(Context);

export { withProvider, useOnlineOpenPaymentFlow };
export default {
    withContext: withContext(Context),
    withProvider: Component =>
        compose(
            LicenseSigningProcessContext.withContext(state => ({
                article: state.article,
                canChangePaymentMethod: get(state, 'all.onlineOpenPayment.canChangePaymentMethod', true),
                canDeleteDraft: get(state, 'all.onlineOpenPayment.canDeleteDraft', false),
                chinaOaEnabled: get(state, 'all.onlineOpenPayment.chinaOaEnabled', false),
                confirmPayment: state.confirmPayment,
                confirmPaymentMethod: state.confirmPaymentMethod,
                deleteOnlineOpenDraft: state.deleteOnlineOpenDraft,
                isOrderExisting: state.all.onlineOpenPayment.isEditOrderFlow,
                journal: state.journal,
                onConfirmSubstep: state.onConfirmSubstep,
                onLoadSubstep: state.onLoadSubstep,
                onPaymentCheckout: state.onPaymentCheckout,
                order: state.all.onlineOpenPayment.order,
                reload: state.reload,
            })),
            withConfirmLeaving,
            withAuth,
            withProvider,
        )(Component),
};
